<template>
  <div>
    <a-form class="form-container" layout="inline">
      <div
        v-if="userInfo?.orderPart === 1"
        class="upload"
        @click="handleUpload"
      >
        <UploadIcon class="icon" />
        <span class="icon-label">
          {{ $t('shopping_cart.batch_upload') }}
        </span>
      </div>

      <div @click="handleClear" class="clear" v-if="selectedRowKeys.length > 0">
        <DeleteIcon class="icon" />
        <a>{{ $t('shopping_cart.clear') }}</a>
      </div>

      <div class="filter">
        <a-form-item style="margin-right: 0;">
          <a-input-search
            @search="handleSearch"
            @change="handleModelChange"
            v-model:value="queryParam.modelNum"
            :placeholder="$t('shopping_cart.search_by_part_no')"
          >
            <template #enterButton>
              <a-button :style="getSearchBtn1Style()">
                <template #icon>
                  <SearchIcon :isActive="!!queryParam.modelNum" />
                </template>
              </a-button>
            </template>
          </a-input-search>
        </a-form-item>
        <a-divider type="vertical" style="height: 32px; margin: 0 40px;" />
        <a-form-item>
          <a-input-search
            @search="handleSearch"
            @change="handleNameChange"
            v-model:value="queryParam.partName"
            :placeholder="$t('shopping_cart.search_by_part_name')"
          >
            <template #enterButton>
              <a-button :style="getSearchBtn2Style()">
                <template #icon>
                  <SearchIcon :isActive="!!queryParam.partName" />
                </template>
              </a-button>
            </template>
          </a-input-search>
        </a-form-item>
      </div>
    </a-form>

    <div class="table-container">
      <a-table
        :pagination="false"
        :columns="columns"
        size="middle"
        row-key="id"
        :loading="loading"
        :data-source="dataSource"
        :row-selection="{
          selectedRowKeys: selectedRowKeys,
          onChange: onSelectChange
        }"
      >
        <template #quantity="{record}">
          <QuantityInput
            :quantity="record.quantity"
            @quantity-change="
              quantity => handleQuantityChange(record, quantity)
            "
          />
        </template>
        <template #vehicleName="{text}">
          {{ text ? $t(text) : '' }}
        </template>
        <template #groupName="{text}">
          {{ text ? $t(text) : '' }}
        </template>

        <template #name="{text, record}">
          <a @click="gotoDiagramDetail(record)">{{ $t(text || '') }}</a>
        </template>

        <template #unit="{text}">
          {{ $t(text || '') }}
        </template>

        <template #modelNum="{text, record}">
          <a @click="gotoDiagramDetail(record)">{{ text }}</a>
        </template>

        <template #part-code="{text, record}">
          <a @click="gotoDiagramDetail(record)">{{ text }}</a>
        </template>

        <template #[`${col}-title`] v-for="col in colTitle" :key="col">
          {{ $t(`shopping_cart.sc_${col}`) }}
        </template>

        <template #unitPrice="{text}">
          {{ text !== null ? numeral(text || 0).format('$0,0.00') : '' }}
        </template>

        <template #totalPrice="{text}">
          {{ text !== null ? numeral(text || 0).format('$0,0.00') : '' }}
        </template>

        <template #action="{record}">
          <a-popconfirm
            @confirm="handleDelete(record)"
            :title="$t('shopping_cart.delete_confirm')"
          >
            <DeleteIcon class="icon" />
          </a-popconfirm>
        </template>

        <template #remark="{text, record}">
          <div class="editable-cell">
            <div v-if="record.editable" class="editable-cell-input-wrapper">
              <a-textarea
                :maxLength="150"
                v-model:value="record.remark"
                @pressEnter="handleSave(record)"
              />
              <check-outlined
                class="editable-cell-icon-check"
                @click="handleSave(record)"
              />
            </div>
            <div v-else class="editable-cell-text-wrapper">
              {{ text || ' ' }}
              <edit-outlined
                class="editable-cell-icon"
                @click="handleEdit(record)"
              />
            </div>
          </div>
        </template>

        <template #consignee="{text, record}">
          <div class="editable-cell">
            <div v-if="record.conEditable" class="editable-cell-input-wrapper">
              <a-select v-model:value="record.consignee">
                <a-select-option
                  v-for="c in consigneeDropdownList"
                  :key="c.id"
                  :value="c.companyName"
                  >{{ c.companyName }}</a-select-option
                >
              </a-select>
              <check-outlined
                class="editable-cell-icon-check"
                @click="handleSave(record)"
              />
            </div>
            <div v-else class="editable-cell-text-wrapper">
              {{ text || ' ' }}
              <edit-outlined
                class="editable-cell-icon"
                @click="handleConsigneeEdit(record)"
              />
            </div>
          </div>
        </template>
      </a-table>
    </div>

    <div class="table-summary" v-if="selectedRowKeys.length > 0">
      <div>{{ $t('shopping_cart.total_price') }}</div>
      <div class="price">{{ totalSummary }}</div>

      <a-button
        v-if="userInfo?.orderPart === 1"
        :loading="isSubmitting"
        @click="handleSubmitOrder"
        class="submit-btn"
        type="primary"
        >{{ $t('shopping_cart.submit') }}</a-button
      >
    </div>

    <UploadModal @after-close="handleAfterClose" v-if="showUploadModal" />

    <UploadResultModal
      @after-close="handleAfterResultClose"
      v-if="showResultModal"
    />
  </div>
</template>

<script lang="ts">
import { createVNode, defineComponent } from 'vue';
import { mapState } from 'vuex';
import { message, Modal } from 'ant-design-vue';
import {
  CheckOutlined,
  EditOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons-vue';
import numeral from 'numeral';
import cloneDeep from 'lodash.clonedeep';
import SearchIcon from '@/components/icons/SearchIcon.vue';
import UploadIcon from '@/components/icons/UploadIcon.vue';
import UploadModal from './UploadModal.vue';
import UploadResultModal from './UploadResultModal.vue';
import DeleteIcon from '@/components/icons/DeleteIcon.vue';
import QuantityInput from '@/components/quantity-input/QuantityInput.vue';
import {
  getShoppingcartPage,
  deleteShoppingcart,
  modifyShoppingcart,
  submitOrder,
  deleteShoppingcartItemById
} from '@/api/apis';

interface PartCartProps {
  dataSource: any[];
  originDataSource: any[];
  selectedRowKeys: any[];
  [key: string]: any;
}

export default defineComponent({
  name: 'PartCart',
  components: {
    SearchIcon,
    UploadIcon,
    UploadModal,
    UploadResultModal,
    QuantityInput,
    DeleteIcon,
    CheckOutlined,
    EditOutlined
  },
  props: {
    consigneeDropdownList: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data(): PartCartProps {
    return {
      numeral,
      queryParam: {
        partName: '',
        modelNum: ''
      },
      selectedRowKeys: [],
      loading: false,
      columns: [
        {
          title: this.$t('shopping_cart.vehicle_name'),
          dataIndex: 'vehicleName',
          width: 100,
          slots: { title: 'pvehicle_name-title', customRender: 'vehicleName' }
        },
        {
          title: this.$t('shopping_cart.sc_part_type'),
          dataIndex: 'groupName',
          width: 100,
          slots: { title: 'part_type-title', customRender: 'groupName' }
        },
        {
          title: this.$t('shopping_cart.sc_name'),
          dataIndex: 'name',
          width: 160,
          slots: { title: 'name-title', customRender: 'name' }
        },
        {
          title: this.$t('shopping_cart.sc_part_no'),
          dataIndex: 'modelNum',
          width: 180,
          slots: { customRender: 'modelNum', title: 'part_no-title' }
        },
        {
          title: this.$t('shopping_cart.sc_part_code'),
          dataIndex: 'partCode',
          width: 100,
          slots: { title: 'part_code-title', customRender: 'part-code' }
        },
        {
          title: this.$t('shopping_cart.sc_unit'),
          dataIndex: 'unit',
          width: 100,
          slots: { customRender: 'unit', title: 'unit-title' }
        },
        {
          dataIndex: 'quantity',
          width: 150,
          slots: { customRender: 'quantity', title: 'quantity-title' }
        },
        // {
        //   dataIndex: 'unitPrice',
        //   width: 100,
        //   slots: { customRender: 'unitPrice', title: 'unit_price-title' }
        // },
        // {
        //   dataIndex: 'totalPrice',
        //   width: 100,
        //   slots: { customRender: 'totalPrice', title: 'total_price-title' }
        // },
        {
          dataIndex: 'consignee',
          width: 140,
          slots: { title: 'consignee-title', customRender: 'consignee' }
        },
        {
          dataIndex: 'remark',
          width: 120,
          slots: { title: 'remark-title', customRender: 'remark' }
        },
        {
          width: 100,
          slots: { title: 'action-title', customRender: 'action' }
        }
      ],
      isSubmitting: false,
      colTitle: [
        'part_type',
        'name',
        'part_no',
        'part_code',
        'unit',
        'quantity',
        'unit_price',
        'total_price',
        'action',
        'consignee',
        'remark'
      ],
      dataSource: [],
      showUploadModal: false,
      showResultModal: false,
      originDataSource: [],

      isDeleting: false
    };
  },
  created() {
    this.loadData();
  },
  computed: {
    totalSummary() {
      const items = this.dataSource.filter(s =>
        this.selectedRowKeys.includes(s.id)
      );

      let total = numeral(0);

      items.forEach(s => {
        total = total.add(s.totalPrice || 0);
      });

      const value = total.value();

      return value ? total.format('$0,0.00') : '';
    },
    ...mapState(['userInfo'])
  },
  methods: {
    handleClear() {
      Modal.confirm({
        // title: 'Are you sure delete this task?',
        icon: createVNode(ExclamationCircleOutlined),
        content: this.$t('shopping_cart.delete_confirm'),
        okText: this.$t('shopping_cart.confirm'),
        cancelText: this.$t('shopping_cart.cancel'),
        onOk: async () => {
          const res = await deleteShoppingcartItemById({
            ids: this.selectedRowKeys.join(',')
          });

          if (res.success) {
            this.selectedRowKeys = [];
            this.loadData();
          }
        }
      });
    },
    handleSearch() {
      this.dataSource = this.originDataSource.filter(s => {
        const modelBoolean =
          (this.queryParam.modelNum &&
            s.modelNum
              ?.toUpperCase()
              .indexOf(this.queryParam.modelNum.toUpperCase()) > -1) ||
          !this.queryParam.modelNum;
        const nameBoolean =
          (this.queryParam.partName &&
            this.$t(s.name || '')
              .toUpperCase()
              .indexOf(this.queryParam.partName.toUpperCase()) > -1) ||
          !this.queryParam.partName;

        return modelBoolean && nameBoolean;
      });
    },
    handleModelChange() {
      if (!this.queryParam.modelNum) {
        this.handleSearch();
      }
    },
    handleNameChange() {
      if (!this.queryParam.partName) {
        this.handleSearch();
      }
    },
    async handleDelete(record) {
      const res = await deleteShoppingcart({
        id: record.id
      });

      if (res.success) {
        this.dataSource = this.dataSource.filter(
          (s: any) => s.id !== record.id
        );

        this.originDataSource = cloneDeep(this.dataSource);
      }
    },
    gotoDiagramDetail(record) {
      if (record.groupId) {
        this.$router.push(`/part-detail/${record.groupId}`);
      } else if (record.vehicleId) {
        this.$router.push(`/spare-part/${record.vehicleId}`);
      }
    },
    async loadData() {
      this.loading = true;
      const res = await getShoppingcartPage({
        modelNum: this.queryParam.modelNum,
        consignee: this.queryParam.consignee,
        shoppingType: '2,3',
        pageNo: 1,
        pageSize: 2147483647
      });

      if (res.success) {
        this.dataSource = res.result.records || [];

        this.originDataSource = cloneDeep(this.dataSource);
      }

      this.loading = false;
    },
    handleUpload() {
      this.showUploadModal = true;
    },
    handleAfterResultClose() {
      this.showResultModal = false;
    },
    handleAfterClose(isCancel) {
      if (!isCancel) {
        this.showResultModal = true;
      }
      this.showUploadModal = false;
    },
    onSelectChange(selectedRowKeys) {
      this.selectedRowKeys = selectedRowKeys;
    },
    getSearchBtn1Style() {
      const isActive = !!this.queryParam.modelNum;
      return {
        width: 'auto',
        padding: '0 15px',
        'background-color': !isActive ? '#E5EAF4' : '#039'
      };
    },
    getSearchBtn2Style() {
      const isActive = !!this.queryParam.partName;
      return {
        width: 'auto',
        padding: '0 15px',
        'background-color': !isActive ? '#E5EAF4' : '#039'
      };
    },
    async handleSubmitOrder() {
      const items = this.dataSource
        .filter(s => this.selectedRowKeys.includes(s.id))
        .map(s => {
          return {
            id: s.id,
            quantity: s.quantity,
            remark: s.remark
          };
        });

      this.isSubmitting = true;
      const res = await submitOrder(items);

      if (res.success) {
        this.selectedRowKeys = [];
        this.loadData();
      } else {
        message.error(this.$t(res.message));
      }

      this.isSubmitting = false;
    },
    async handleSave(record) {
      const res = await modifyShoppingcart({
        ...record
      });

      if (res.success) {
        record.editable = false;
        record.conEditable = false;
        const oIndex = this.originDataSource.findIndex(s => s.id === record.id);
        if (oIndex > -1) {
          this.originDataSource.splice(oIndex, 1, { ...record });
        }
      }
    },
    handleEdit(record) {
      record.editable = true;
    },
    handleConsigneeEdit(record) {
      record.conEditable = true;
    },
    async handleQuantityChange(record, quantity) {
      const res = await modifyShoppingcart({
        ...record,
        quantity
      });

      if (res.success) {
        record.totalPrice = record.unitPrice
          ? numeral(quantity)
              .multiply(record.unitPrice || 0)
              .value()
          : null;
      }

      record.quantity = quantity;
    }
  }
});
</script>

<style lang="scss" scoped>
.form-container {
  margin-bottom: 30px;
  display: flex;
  align-items: center;

  .clear {
    margin-left: 24px;
    a {
      margin-left: 8px;
    }
    cursor: pointer;
  }

  .upload {
    display: flex;
    align-items: center;

    cursor: pointer;
    color: $primary-color;
    .icon-label {
      margin-left: 10px;
    }

    &:hover {
      color: $primary-hover-color;
    }
  }

  .filter {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: flex-end;

    &::v-deep(.ant-input) {
      width: 300px;
    }
  }
}

.table-container {
  margin-bottom: 40px;
  overflow: auto;
}

.table-summary {
  margin-bottom: 40px;
  background: #e5eaf4;
  height: 60px;

  display: flex;
  align-items: center;
  justify-content: flex-end;

  .price {
    font-size: $secondary-fontsize;
    color: $primary-color;
    font-weight: bold;
    margin: 0 40px 0 20px;
  }

  .submit-btn {
    padding: 0 50px;
    height: 100%;
  }
}
</style>
